import {addInventoryToCart, getInventory} from "../../api/Api";
import {useEffect, useRef, useState} from 'react';
import {Button, Checkbox, Table, message, Row, Col} from "antd";
import {CategoryType, ProductType, ProductSubType, UnitType} from "../../api/Constants";
import {AddCartWidget} from "./AddCartWidget";
import {getBaseColumnSearchProps, getColumnSearchProps} from "../widget/SearchProps";
import {toFSDateTime} from "../../util/timeutils";
import {Link} from "react-router";
import {BookStatusTag} from "./BookStatusTag";
import {fixedToDisplayUnit, displayToFixedUnit} from "../../util/unitutils";
import {saveAs} from 'file-saver';

const getCustomSearchProps = (dataIndex, searchInput, setColumnSearch) => ({
    ...getBaseColumnSearchProps(dataIndex, searchInput, setColumnSearch),
    onFilter: (value, record) => {
        if (dataIndex === "type") {
            const typeString = ProductType.Label[record.type]
            return typeString.toLowerCase().includes(value.toLowerCase());
        } else if (dataIndex === "subtype") {
            const typeString = ProductSubType.Label[record.subtype];
            return typeString.toLowerCase().includes(value.toLowerCase());
        }
        return record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
    },
});

export const InventoryTable = ({onApiFail, addToCart, refresh, lowInventoryAlert=false, disableLinks=false, enableCSV=false, props}) => {
    const [inventory, setInventoryList] = useState([]);
    const [messageApi, contextHolder] = message.useMessage()
    const [showHidden, setShowHidden] = useState(false);

    useEffect(() => {
        getInventory(showHidden).then((x) => {
            let tableData = x.map((val, index) => {
                return {...val, key: index}
            });
            if (lowInventoryAlert === true) {
                console.log(`filtering`);
                tableData = tableData.filter(row => ((row.enableAlert===1) && (row.quantity <= row.alertQuantity )));
            }
            setInventoryList(tableData);
        }).catch (e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }, [refresh, onApiFail, lowInventoryAlert, showHidden]);

    const addCart = (itemRecord) => (value) => {
        console.log(`adding ${value} of item ${itemRecord.id} to cart`);
        const fixedQuantity = displayToFixedUnit(value, itemRecord.unit);
        addInventoryToCart(itemRecord.id, fixedQuantity).then(r => {
            if (addToCart) {
                addToCart(itemRecord, fixedQuantity)
            }
            messageApi.open({
                type: 'success',
                content: `${itemRecord.name} added to cart`
            });
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
            messageApi.open({
                type: 'error',
                content: `Failed to add ${itemRecord.name} to cart`
            });
        })
    }

    const onShowHidden = (e) => {
        setShowHidden(e.target.checked);
    }

    const searchInput = useRef(null);

    const getDisplayQuantity = (val, record) => {
        const displayQuantity = fixedToDisplayUnit(val, record.unit);
        if (record.category === CategoryType.Wholesale) {
            return `${displayQuantity} ${UnitType.Label[record.unit]}                  `
        }
        return displayQuantity;
    }

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (val, record, index) => {
                // disable links for certain views,  it becomes a click hazard and loses the page context
                if (disableLinks === true) {
                    return val;
                }
                return <Link to={`/inventory/${record.id}`}>{val}</Link>
            },
            sorter: (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
            ...getColumnSearchProps('name', searchInput)
        },
        {
            title: 'Sku',
            dataIndex: 'sku',
            key: 'sku',
            sorter: (a, b) => a.sku.toLowerCase().localeCompare(b.sku.toLowerCase()),
            ...getColumnSearchProps('sku', searchInput)
        },
        {
            title: 'Description',
            dataIndex: 'inventoryDescription',
            key: 'description',
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (val, record, index) => {
                return getDisplayQuantity(val, record);
            },
            sorter: (a, b) => a.quantity - b.quantity
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (val, record, index) => {
                return CategoryType.Label[val]
            },
            sorter: (a, b) => a.category - b.category
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (val, record, index) => {
                return ProductType.Label[val];
            },
            sorter: (a, b) => a.type - b.type,
            ...getCustomSearchProps('type', searchInput)
        },
        {
            title: 'Sub Type',
            dataIndex: 'subtype',
            key: 'subtype',
            render: (val, record, index) => {
                return ProductSubType.Label[val];
            },
            sorter: (a, b) => a.subtype - b.subtype,
            ...getCustomSearchProps('subtype', searchInput)
        },
        {
            title: 'Created',
            dataIndex: 'created',
            key: 'created',
            render: (val, record, index) => {
                return toFSDateTime(new Date(val))
            },
            sorter: (a, b) => {
                const {created: aCreated} = a;
                const {created: bCreated} = b;
                return (new Date(aCreated))- (new Date(bCreated))
            },
        },
        {
            title: 'Book',
            dataIndex: 'book',
            key: 'book',
            render: (val, record, index) => {
                return <BookStatusTag book={val} />
            },
            sorter: (a, b) => a.book - b.book
        }
    ];

    if (addToCart !== undefined) {
        columns.push({
            title: '',
            dataIndex: 'id',
            key: 'id',
            render: (val, record, index) => {
                return <AddCartWidget onAdjust={addCart(record)} item={record}><Button>Add to Cart</Button></AddCartWidget>
            }
        })
    }

    const sanitizeString = (string) => {
        if ((string === null) || (string === undefined) || (string.trim().length === 0)) {
            return "none"
        } else {
            return string;
        }
    }

    const handleDownload = () => {
        // name, description, category, sku, id, productId, quantity, created, cost, book
        // wholesale has cost defined as bulkCost
        // packaged
        // supplies has tariff and shipping
        const now = new Date();
        const lineData = ["Name, Description, Category, Sku, id, productId, quantity, created, cost, book, tariff, shipping\n"];
        inventory.forEach(l => {
            const name = sanitizeString(l.name);
            const {inventoryDescription, created} = l;
            const description = sanitizeString(inventoryDescription);
            const sku = sanitizeString(l.sku);
            const displayQuantity = getDisplayQuantity(l.quantity, l);
            let cost;
            if (l.category === CategoryType.Wholesale) {
                cost = l.bulkCost;
            } else {
                cost = (parseFloat(l.cost)/parseFloat(l.startQuantity)).toFixed(2);
            }
            let lineBuffer = `${name}, ${description}, ${CategoryType.Label[l.category]}, ${sku}, ${l.id}, ${l.itemId}, ${displayQuantity}, ${toFSDateTime(new Date(created))}, ${cost}, ${l.book}, `;

            // need to append column by column now for each category
            // tariff
            if (l.category === CategoryType.Supplies) {
                lineBuffer += `${l.tariff}, `
            } else {
                // everything else
                lineBuffer += `n/a, `
            }

            // shipping
            if (l.category === CategoryType.Supplies) {
                lineBuffer += `${l.shipping}`
            } else {
                // everything else
                lineBuffer += `n/a`
            }

            lineBuffer += "\n"; // newline
            lineData.push(lineBuffer);
        })
        const file = new Blob(lineData, { type: 'text/plain;charset=utf-8' });

        const filename = `inventory_${toFSDateTime(now)}.csv`
        saveAs(file, filename);
    };

    const tablePagination = {
        defaultPageSize: 50,
        showSizeChanger: true,
        pageSizeOptions: [50, 100, 150, 200]
    }

    const rowStyle= {
        marginTop: '5px',
        marginBottom: '5px'
    }

    const colStyle= {
        margin: '5px'
    }

    return <div>
        {contextHolder}
        <div style={{textAlign: 'right'}}>
            <Row>
                <Col style={colStyle}>
                    <Checkbox checked={showHidden} onChange={onShowHidden} />
                </Col>
                <Col style={colStyle}>
                    <b>Show Hidden</b>
                </Col>
            </Row>
            {(enableCSV) &&
                <Row style={rowStyle}>
                    <Button disabled={inventory.length <= 0} onClick={handleDownload}>Save CSV</Button>
                </Row>
            }
        </div>
        <Table dataSource={inventory} columns={columns} pagination={tablePagination}/>
    </div>
}